import React, { useContext, useEffect, useRef, useState } from 'react';
import { LoginContext } from '../../../App';

import './MUnitsCategoriesTable.css';
import { fetchCategoriesData, getCategoriesCSV, importingCategoriesCSVFile } from '../../../services/b2b-indicators-apiService';
import B2BTableInMemory from '../../b2b-common/B2BTableInMemory';
import { Category, Download, FileUpload } from '@mui/icons-material';
import CategoryModal from '../modal/CategoryModal';
import { callAPIWhileSettingState, get_YYMMDDHHMM } from '../../b2b-common/helpers';
import B2BDialog from '../../b2b-common/B2BDialog';

const MUnitsCategoriesTable = () => {
    const { loginData } = useContext(LoginContext);
    const [categories, setCategories] = useState(null);
    const [openNewCategoryDialog, setOpenNewCategoryDialog] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [refreshTable, setRefreshTable] = useState(false);

    useEffect(() => {
        const getCategories = async () => {
            try {
                const { accessToken } = loginData;
                const data = await fetchCategoriesData(accessToken);
                setCategories(data);
            } catch (error) {
                console.error(error);
            }
        };

        getCategories();
    }, [loginData, refreshTable]);

    const headers = [
        { label: 'Name', align: 'left' },
        { label: 'Description', align: 'left' },
        { label: 'Units #', align: 'center' },
    ];

    const rows = categories ? categories.map((category) => ({
        id: category.measurementUnitCategoryId,
        cells: [
            { content: `${category.measurementUnitCategoryName_label}`, align: 'left' },
            { content: `${category.measurementUnitCategoryDescription_label}`, align: 'left' },
            { content: `${category.units ? category.units.length : 0}`, align: 'center' },
        ]
    })) : [];

    const handleSelectCategory = (categoryId) => {
        const selectedCategory = categories.find((category) => category.measurementUnitCategoryId === categoryId.id);
        setSelectedCategory(selectedCategory);
        setOpenNewCategoryDialog(true);
    }

    // Downloading CSV

    const [downloadingCSV, setDownloadingCSV] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogError, setDialogError] = useState(null);

    const handleDownloadingCSV = async () => {
        const fileName = 'b2b-categories-' + get_YYMMDDHHMM() + '.csv';
        const paramsArray = [
            loginData.accessToken,
            ';',
            fileName
        ];
        try {
            await callAPIWhileSettingState(getCategoriesCSV, paramsArray,
                setDownloadingCSV, null);
        } catch (error) {
            setDialogOpen(true);
            setDialogError('Failed to download csv: ' + error.message);
        } finally {
            setDownloadingCSV(false);
        }
    }

    // Importing CSV

    const csvFileInputRef = useRef(null);
    const [processingCSVFile, setProcessingCSVFile] = useState(false);

    const handleUploadingCSV = () => {
        csvFileInputRef.current.click();
    }

    const handleCSVFileUpload = async (e) => {
        const paramsArray = [
            e.target.files[0],
            ';',
            loginData.accessToken
        ];
        try {
            await callAPIWhileSettingState(importingCategoriesCSVFile, paramsArray,
                setProcessingCSVFile, onCSVImported);
            setRefreshTable(prev => !prev);
        } catch (error) {
            setDialogOpen(true);
            setDialogError('Failed to import csv: ' + error.message);
        } finally {
            e.target.value = null;
        }
    }

    const onCSVImported = () => {
        setDialogOpen(true);
        setDialogError(null);
    }

    const buttons = [
        {
            label: 'New Category',
            disabledText: 'New Category',
            onClick: () => { setOpenNewCategoryDialog(true) },
            color: 'secondary',
            variant: 'contained',
            key: '1',
            icon: <Category />,
        },
        {
            label: 'Get CSV',
            disabledText: 'Get CSV',
            onClick: () => { handleDownloadingCSV() },
            color: 'secondary',
            variant: 'contained',
            key: '2',
            icon: <Download />,
        },
        {
            label: 'Import CSV',
            disabledText: 'Import CSV',
            onClick: () => { handleUploadingCSV() },
            color: 'secondary',
            variant: 'contained',
            disabled: processingCSVFile,
            isLoading: processingCSVFile,
            key: '3',
            icon: <FileUpload />,
        },
    ]

    return (
        <>
            <B2BTableInMemory
                headers={headers}
                rows={rows}
                className={"measurement-units-categories-list-table"}
                numRows={10}
                stickyHeader={false}
                onRowClick={handleSelectCategory}
                buttons={buttons}
            />
            <input
                ref={csvFileInputRef}
                type="file"
                style={{ display: 'none' }}
                onChange={handleCSVFileUpload}
            />
            <CategoryModal
                open={openNewCategoryDialog}
                toChangeCategory={selectedCategory}
                onUpdate={() => {
                    setOpenNewCategoryDialog(false);
                    setRefreshTable(prev => !prev);
                    setSelectedCategory(null);
                }}
                onClose={() => {
                    setOpenNewCategoryDialog(false);
                    setSelectedCategory(null);
                }}
            />
            <B2BDialog
                open={dialogOpen}
                onClose={() => {
                    setDialogOpen(false);
                    setDialogError(null);
                }}
                title={dialogError ? 'Error downloading CSV' : 'CSV Downloaded'}
            >
                <p>{dialogError || "The csv file has been downloaded successfully."}</p>
            </B2BDialog>
        </>
    );
};

export default MUnitsCategoriesTable;